import type { Fill } from 'api';
import { parseExcelColorToCss } from 'components/SpreadsheetNotes/utilities/parseExcelColorToCss';

const getFillStyle = (formatting?: Fill): React.CSSProperties => {
  if (!formatting || (!formatting.bg_color && !formatting.fg_color)) {
    return {};
  }
  const { bg_color: rawBackgroundColor, fg_color: rawForegroundColor, pattern_type: patternType } = formatting;
  const backgroundColor = parseExcelColorToCss(rawBackgroundColor);
  const foregroundColor = parseExcelColorToCss(rawForegroundColor);

  if (patternType === 'solid') {
    const solidColor = foregroundColor ?? backgroundColor;
    if (!solidColor) {
      return {};
    }

    return {
      backgroundColor: solidColor,
    };
  }

  // If no pattern or solid pattern, just return background color
  if (!patternType || (!foregroundColor && backgroundColor)) {
    return {
      backgroundColor: backgroundColor as string,
    };
  }

  if (!backgroundColor || !foregroundColor) {
    return {};
  }

  // Base style with background color
  const baseStyle: React.CSSProperties = {
    backgroundColor,
  };

  // Add pattern-specific background images
  switch (patternType) {
    case 'lightHorizontal':
    case 'horizontal':
      return {
        ...baseStyle,
        backgroundImage: `repeating-linear-gradient(0deg, ${foregroundColor}, ${foregroundColor} 1px, transparent 1px, transparent 4px)`,
      };

    case 'lightVertical':
    case 'vertical':
      return {
        ...baseStyle,
        backgroundImage: `repeating-linear-gradient(90deg, ${foregroundColor}, ${foregroundColor} 1px, transparent 1px, transparent 4px)`,
      };

    case 'lightDown':
    case 'darkDown':
    case 'diagonal':
      return {
        ...baseStyle,
        backgroundImage: `repeating-linear-gradient(45deg, ${foregroundColor}, ${foregroundColor} 1px, transparent 1px, transparent 4px)`,
      };

    case 'lightUp':
    case 'darkUp':
      return {
        ...baseStyle,
        backgroundImage: `repeating-linear-gradient(-45deg, ${foregroundColor}, ${foregroundColor} 1px, transparent 1px, transparent 4px)`,
      };

    case 'lightGrid':
    case 'grid':
      return {
        ...baseStyle,
        backgroundImage: `
          repeating-linear-gradient(0deg, ${foregroundColor}, ${foregroundColor} 1px, transparent 1px, transparent 4px),
          repeating-linear-gradient(90deg, ${foregroundColor}, ${foregroundColor} 1px, transparent 1px, transparent 4px)
        `,
      };

    case 'dots':
    case 'lightDots':
      return {
        ...baseStyle,
        backgroundImage: `radial-gradient(circle at 50% 50%, ${foregroundColor} 1px, transparent 1px)`,
        backgroundSize: '4px 4px',
      };

    case 'gray125':
    case 'gray0625':
      return {
        ...baseStyle,
        backgroundImage: `radial-gradient(circle at 25% 25%, ${foregroundColor} 0.5px, transparent 0.5px)`,
        backgroundSize: '2px 2px',
      };

    default:
      return baseStyle;
  }
};

export default getFillStyle;
